import { FC } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  ListItem,
  UnorderedList,
} from '@chakra-ui/react';
import { formatActivityType } from '@shinetools/ape-code-library';

import Callout from 'components/_core/Callout';
import SunshineIcon from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import Loader from 'components/Loader';
import { formatAddress } from 'helpers/address';
import { formatDate, PPP } from 'helpers/date';
import { formatSIRET } from 'helpers/siret';

import { useCompanyDetails } from '../../hooks/useCompanyDetails';
import { locales } from '../../locales';
import CompanyDetailsRow from '../CompanyDetailsRow';

const CompanyDetails: FC = () => {
  const { company, loading, missingData } = useCompanyDetails();

  if (loading) {
    return <Loader />;
  }

  if (!company) {
    return null;
  }

  return (
    <>
      {missingData.length > 0 ? (
        <Callout marginBottom="space-32" variant="warning">
          <Callout.Content>
            {locales.companyDetails.callout.title}
            <UnorderedList>
              {missingData.map((key) => (
                <ListItem key={key}>
                  {locales.companyDetails.missingData[key]}
                </ListItem>
              ))}
            </UnorderedList>
            <br />
            {locales.companyDetails.callout.conclude}
            <br />
            <Callout.Button
              as="a"
              href="https://app.shine.fr/help/question"
              marginTop="space-8"
              onClick={() => {}}
              target="_blank"
              variant="inline-secondary"
            >
              {locales.companyDetails.callout.cta}
            </Callout.Button>
          </Callout.Content>
        </Callout>
      ) : null}

      <Accordion allowToggle marginBottom="space-32">
        <AccordionItem
          backgroundColor="white"
          borderBottomColor="grey.2"
          borderRadius="radius-8"
          borderWidth="1px"
        >
          {({ isExpanded }) => (
            <>
              <h2>
                <AccordionButton
                  _expanded={{
                    borderBottomColor: 'grey.300',
                    borderBottomWidth: '1px',
                  }}
                  _hover={{ bg: 'grey.200' }}
                  padding="space-16"
                >
                  <Text fontWeight="weight-500" variant="primary">
                    {locales.details.title}
                  </Text>

                  <SunshineIcon
                    color="light"
                    name="chevron-down"
                    size="icon-24"
                    transform={isExpanded ? 'rotate(180deg)' : ''}
                    transition="all .25s ease-out"
                  />
                </AccordionButton>
              </h2>

              <AccordionPanel
                backgroundColor="white"
                borderBottomLeftRadius="radius-8"
                borderBottomRightRadius="radius-8"
                gap={0}
                padding={0}
              >
                <CompanyDetailsRow
                  title={locales.details.legalName}
                  value={company.legalName}
                />
                <CompanyDetailsRow
                  title={locales.details.address}
                  value={formatAddress(company)}
                />
                <CompanyDetailsRow
                  title={locales.details.creationDate}
                  value={
                    company.registrationDate !== ''
                      ? formatDate(new Date(company.registrationDate), PPP)
                      : '-'
                  }
                />
                <CompanyDetailsRow
                  title={locales.details.SIRET}
                  value={formatSIRET(company.legalRegistrationNumber)}
                />
                <CompanyDetailsRow
                  title={locales.details.activity}
                  value={formatActivityType(company.activityType)}
                />
                <CompanyDetailsRow
                  hasBorder={false}
                  title={locales.details.code}
                  value={company.activityType}
                />
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      </Accordion>
    </>
  );
};

export default CompanyDetails;
